import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { Eye, FlaskConical, X } from "lucide-react";
import { getSandbox, setSandbox } from "../lib/api";

// Entry route opened by Back Office → Curriculum → Preview/Test: /sandbox#<base64 json>. Stores the per-tab token and jumps to the lesson page.
export function SandboxEntry() {
  const [err, setErr] = useState("");
  const [sp] = useSearchParams();
  useEffect(() => {
    try {
      const raw = sp.get("p") || window.location.hash.slice(1);
      const sb = JSON.parse(new TextDecoder().decode(Uint8Array.from(atob(raw), (c) => c.charCodeAt(0))));
      if (!sb?.token || !sb?.target) throw new Error("bad payload");
      setSandbox(sb);
      window.location.replace(sb.target);
    } catch (e) { console.error("[sandbox] entry failed", e); setErr("Invalid sandbox link"); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return <div className="min-h-screen grid place-items-center text-slate-600 font-semibold" data-testid="sandbox-entry">{err || "Opening preview…"}</div>;
}

const TXT = {
  en: { preview: "Preview Mode — read-only, nothing is saved", test: "Test Mode — sandbox, no real data affected", student: "Student view", trainer: "Trainer view", exit: "Exit", lesson: "Lesson" },
  ar: { preview: "وضع المعاينة — للقراءة فقط، لا يُحفظ شيء", test: "وضع التجربة — بيئة معزولة، لا تتأثر البيانات الحقيقية", student: "عرض الطالب", trainer: "عرض المدرّب", exit: "خروج", lesson: "الدرس" },
};

export function SandboxBanner() {
  const loc = useLocation();
  const [sb, setSb] = useState(getSandbox());
  useEffect(() => { const sync = () => setSb(getSandbox()); window.addEventListener("buma:sandbox-changed", sync); return () => window.removeEventListener("buma:sandbox-changed", sync); }, []);
  if (!sb || loc.pathname === "/sandbox") return null;
  const lang = (localStorage.getItem("tt:lang") || sb.lang || "en") === "ar" ? "ar" : "en";
  const L = TXT[lang];
  const preview = sb.mode === "preview";
  const tone = preview ? "bg-amber-500 text-amber-950 border-amber-600" : "bg-rose-600 text-white border-rose-700";
  const exit = () => { setSandbox(null); window.close(); setTimeout(() => window.location.replace("/"), 150); };
  const title = (lang === "ar" && sb.lesson?.title_ar) || sb.lesson?.title || "";
  const cur = (lang === "ar" && sb.curriculum?.name_ar) || sb.curriculum?.name || "";
  return (
    <>
      <div aria-hidden className={`pointer-events-none fixed inset-0 z-[9998] border-[5px] ${preview ? "border-amber-400/80" : "border-rose-500/80"}`} />
      <div dir={lang === "ar" ? "rtl" : "ltr"} data-testid="sandbox-banner" data-mode={sb.mode} data-view={sb.view} className={`fixed top-0 inset-x-0 z-[9999] flex items-center justify-center gap-3 px-4 py-1.5 text-xs sm:text-sm font-bold shadow-md border-b ${tone}`}>
        {preview ? <Eye size={15} /> : <FlaskConical size={15} />}
        <span data-testid="sandbox-banner-text">{preview ? L.preview : L.test}</span>
        <span className="opacity-80 hidden sm:inline">· {sb.view === "trainer" ? L.trainer : L.student}{cur ? ` · ${cur}` : ""}{sb.lesson?.order ? ` › ${L.lesson} ${sb.lesson.order}` : ""}{title ? ` — ${title}` : ""}</span>
        <button data-testid="sandbox-exit" onClick={exit} className={`ms-2 inline-flex items-center gap-1 rounded-full px-3 py-0.5 text-xs font-bold ${preview ? "bg-amber-950 text-amber-100" : "bg-white text-rose-700"}`}><X size={12} /> {L.exit}</button>
      </div>
      <div className="h-8" />
    </>
  );
}

export const openSandboxTab = (sb) => {
  const payload = btoa(String.fromCharCode(...new TextEncoder().encode(JSON.stringify(sb))));
  window.open(`/sandbox?p=${encodeURIComponent(payload)}`, "_blank", "noopener");
};
